<template>
  <div class="flex flex-wrap items-end mr-2" ref="search">
    <div class="p-2.5">
      <AutoComplete
        ref="locationSearchRef"
        v-model="locationSearch"
        :suggestions="locationSuggestions"
        @complete="searchLocation"
        @item-select="onLocationSelect"
        optionLabel="label"
        :placeholder="searchPlaceholder"
        class="shadow-sm address-autocomplete"
        fluid
        auto-option-focus
        show-empty-message=""
        :panelClass="'w-full max-w-lg'"
        :inputClass="'rounded-md'"
        :pt="{
          root: { class: 'w-full min-w-56' },
          input: { class: 'w-full' },
          listContainer: { style: 'max-height: 18rem;', class: 'w-full max-w-lg p-2' },
        }"
      >
        <template #option="slotProps">
          <div class="flex items-center gap-2 text-sm">
            <i
              :class="[
                'pi mt-0.5',
                slotProps.option.type === 'address' ? 'pi-map-marker text-indigo-500' : 'pi-th-large text-emerald-500'
              ]"
            ></i>
            <div class="flex flex-col">
              <span class="font-medium">{{ slotProps.option.label }}</span>
              <span v-if="slotProps.option.sublabel" class="text-xs text-gray-500">{{ slotProps.option.sublabel }}</span>
            </div>
          </div>
        </template>
      </AutoComplete>
    </div>
    <div class="p-2.5">
      <div class="flex gap-1">
        <div class="flex gap-2">
          <div class="flex flex-col justify-center items-center shadow-sm group">
            <div class="-mb-1 flex bg-linear-to-b from-white/75 to-white rounded-md rounded-b-none">
              <DatePickerButtonBar @select="setDateRangeShortcut" :active-months="activeDateShortcut" :hover-enabled="true" />
            </div>

            <div class="bg-white rounded-md rounded-t-none p-1">
              <DatePicker
                v-model="dateFilter.from"
                data-testid="txn-date-from"
                :date-format="'dd.mm.yy'"
                showIcon
                showClear
                fluid
                iconDisplay="input"
                :placeholder="$t(`messages.transactions.from`)"
                @update:model-value="filterChange()"
                @date-select="onDateSelect(formatDatePickerToISO($event), 'from')"
                class="max-w-[10.35rem]"
                show-button-bar
              >
                <template #buttonbar>
                  <DatePickerButtonBar @select="setDateRangeShortcut" :active-months="activeDateShortcut" />
                </template>
              </DatePicker>
            </div>
          </div>
        </div>
        <div class="flex flex-col justify-end grow">
          <div class="bg-white rounded-md shadow-sm p-1">
            <DatePicker
              v-model="dateFilter.to"
              data-testid="txn-date-to"
              :date-format="'dd.mm.yy'"
              showIcon
              showClear
              fluid
              iconDisplay="input"
              :placeholder="$t(`messages.transactions.to`)"
              @update:model-value="filterChange()"
              @date-select="onDateSelect(formatDatePickerToISO($event), 'to')"
              class="max-w-[10.35rem]"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="p-2.5">
      <ChipMultiSelect
        v-model="objectType"
        data-testid="txn-object-type"
        :options="options['objekti_liik'] || []"
        :label="$t('messages.transactions.fields.objekti_liik')"
        :all-label="$t('messages.transactions.all')"
        :rows="2"
        @update:model-value="filterChange()"
      />
    </div>

    <div class="p-2.5">
      <ChipMultiSelect
        v-model="hasBuildings"
        :options="options['hoonestatud'] || []"
        :label="$t('messages.transactions.fields.hoonestatud')"
        :rows="1"
        fluid
        @update:model-value="filterChange()"
      />
    </div>

    <div class="p-2.5">
      <div class="p-1 rounded-md bg-white shadow-sm border border-gray-200 flex gap-1.5">
        <TransactionEhrComponent
          data-testid="txn-ehr-filters"
          :disabled="!komSelected && !kasiSelected"
          :texts="texts"
          :options="options"
          v-model="ehrValues"
          @update:model-value="filterChange()"
        />
        <TransactionAreaComponent
          :apartment="appartmentArea"
          :area="totalArea"
          :unit="areaUnit"
          @change="
            (value) => {
              appartmentArea = value.apartment
              totalArea = value.area
              areaUnit = value.unit
              filterChange()
            }
          "
        />
        <TransactionPriceComponent
          :from="price.from"
          :to="price.to"
          @change="
            (value) => {
              price = {
                from: value.from,
                to: value.to,
              }
              priceMode = value.mode
              priceUnitType = value.unitType
              filterChange()
            }
          "
        />
      </div>
    </div>
    <div class="flex gap-1 my-3.5">
      <Button
        severity="primary"
        data-testid="txn-search"
        class="h-10 w-10 2xl:w-auto"
        :disabled="submitDisabled"
        :aria-label="$t('messages.search')"
        @click.stop.prevent="submit"
      >
        <span class="pi pi-search" v-if="!loading"></span>
        <span class="pi pi-arrow-clockwise" animation="spin" v-else></span>
        <span class="hidden 2xl:inline">{{ $t('messages.search') }}</span>
      </Button>
      <Button severity="secondary" data-testid="txn-reset" :disabled="submitDisabled" @click.stop.prevent="reset" class="w-10 h-10">
        <span class="pi pi-times" :class="{ 'text-primary-500': !submitDisabled }"></span>
      </Button>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'
import { format, parse, subMonths } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { useHelpers } from '@/composables/useHelpers'
import { useMapStore } from '@/stores/map'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import {
  SEARCH_DEFAULT_UNIT,
  SEARCH_EHR_DEFAULTS,
  SEARCH_SLIDER_LIMITS,
  TRANSACTIONS_FILTERS,
} from '../../constants'
import { createGeoJsonFeature } from '../../services/map/FeatureService'
import { deepCopy } from '../../services/utilities'
import ChipMultiSelect from '@/components/ChipMultiSelect.vue'
import DatePickerButtonBar from './filter/DatePickerButtonBar.vue'
import TransactionAreaComponent from './filter/TransactionAreaComponent.vue'
import TransactionEhrComponent from './filter/TransactionEhrComponent.vue'
import TransactionPriceComponent from './filter/TransactionPriceComponent.vue'

// EHR filter keys that should be fetched from the API
const EHR_DYNAMIC_FILTER_KEYS = [
  'ehitise_valisseina_liik',
  'ehitise_konstruktsioonide_materjal',
  'hooneosa_soojavarustuse_liik',
  'ehitise_vundamendi_liik',
  'ehitise_katuse_materjal',
  'ehitise_valisseina_viimistlus',
  'ehitise_veevarustuse_liik',
  'ehitise_kanalisatsiooni_liik',
  'hooneosa_soojusallika_liik',
  'hooneosa_energiaallika_liik',
  'hooneosa_tualettruumide_liik',
  'hooneosa_pesemisvoimaluse_liik',
]

const { formatDatePickerToISO } = useFormatDate()

const emit = defineEmits(['update:map-features', 'search', 'location-selected'])

const { t } = useI18n()
const transactionStore = useTransactionStore()
const { transactionMapRequestParams } = storeToRefs(transactionStore)
const evaluationStore = useEvaluationStore()

const mapStore = useMapStore()
const { label: activeLabel } = storeToRefs(mapStore)

const { isFilterDirty } = useHelpers()

// Address search state
const locationSearchRef = ref(null)
const locationSearch = ref('')
const locationSuggestions = ref([])

// Detect if user is on Mac
const isMac = computed(() => {
  return typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
})

// Keyboard shortcut placeholder hint
const searchPlaceholder = computed(() => {
  const shortcut = isMac.value ? '⌘F' : 'Ctrl+F'
  return `${t('messages.transactions.address_search')} (${shortcut})`
})

// Location search functions
const searchLocation = async (event) => {
  const query = event.query
  if (query.length < 2) {
    locationSuggestions.value = []
    return
  }
  try {
    const response = await axios.get('/api/search/location', {
      params: { query },
    })
    locationSuggestions.value = Array.isArray(response.data?.results) ? response.data.results : []
  } catch (error) {
    console.error('Location search error:', error)
    locationSuggestions.value = []
  }
}

const onLocationSelect = (event) => {
  const location = event.value
  emit('location-selected', location)
  locationSearch.value = ''
}

// Handle keyboard shortcut for search focus
const handleKeyDown = (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
    event.preventDefault()
    if (locationSearchRef.value) {
      const inputEl = locationSearchRef.value.$el?.querySelector('input')
      if (inputEl) {
        inputEl.focus()
        inputEl.select()
      }
    }
  }
}

const dateFilter = ref({
  from: null,
  to: null,
})
const dateFilterFrom = ref(null)
const dateFilterTo = ref(null)
// Which quick-preset ("N kuud") produced the current range; null once the
// user picks or clears a date manually, so the Tag highlight never lies.
const activeDateShortcut = ref(null)

const objectType = ref([])
const hasBuildings = ref([])
const areaUnit = ref(SEARCH_DEFAULT_UNIT)
const appartmentArea = ref(SEARCH_SLIDER_LIMITS.korteri_pind)
const totalArea = ref(SEARCH_SLIDER_LIMITS.pindala[SEARCH_DEFAULT_UNIT])
const price = ref({
  from: null,
  to: null,
})
const priceMode = ref('total')
const priceUnitType = ref('apartment')
const search = ref(null)
const loading = ref(false)
const submitDisabled = ref(true)
const labels = ref({})
const options = ref({})
const areaDropdownOpen = ref(false)
const komSelected = ref(false)
const kasiSelected = ref(false)

const objectTypeSelectAll = ref(false)

const ehrValues = ref(deepCopy(SEARCH_EHR_DEFAULTS))

const texts = computed(() => {
  return {
    selectvalue: `${t('messages.transactions.choose')}...`,
    optionsselected: 'option(s) selected',
    nothingselected: 'No option selected',
    selectall: t('messages.transactions.choose_all'),
    all: t('messages.transactions.all'),
    selected: t('messages.transactions.chosen'),
    placeholder: `${t('messages.transactions.filter')}...`,
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})

onMounted(async () => {
  document.addEventListener('keydown', handleKeyDown)

  // Load static options from constants as initial/fallback values
  TRANSACTIONS_FILTERS.forEach(item => {
    labels.value[item.key] = item.label || ''
    if (item.options) {
      options.value[item.key] = item.options || []
    }
  })

  // Fetch dynamic EHR filter options from database
  try {
    const response = await axios.get('/api/filter-options/ehr')
    const ehrOptions = response.data

    // Merge dynamic options with static ones (dynamic takes precedence if available)
    EHR_DYNAMIC_FILTER_KEYS.forEach(key => {
      if (ehrOptions[key] && ehrOptions[key].length > 0) {
        options.value[key] = ehrOptions[key]
      }
    })
  } catch (error) {
    // If API fails, keep static options as fallback
    console.warn('Failed to fetch EHR filter options, using static fallback:', error)
  }
})

const selectAllChange = (event, key) => {
  objectTypeSelectAll.value = !objectTypeSelectAll.value
  if (objectTypeSelectAll.value) {
    objectType.value = options.value[key]
  } else {
    objectType.value = []
  }
  filterChange()
}

const onDateSelect = (event, key) => {
  activeDateShortcut.value = null
  if (key === 'from') {
    dateFilterFrom.value = format(event, 'yyyy-MM-dd')
  } else {
    dateFilterTo.value = format(event, 'yyyy-MM-dd')
  }
}

const filterChange = () => {
  // Keep the ISO mirrors honest when a DatePicker is cleared — otherwise the
  // stale string would still ride the next search payload.
  if (!dateFilter.value.from) {
    dateFilterFrom.value = null
  }
  if (!dateFilter.value.to) {
    dateFilterTo.value = null
  }
  if (!dateFilter.value.from || !dateFilter.value.to) {
    activeDateShortcut.value = null
  }

  komSelected.value = !!objectType.value.find(item => item.value === 'KOM')
  kasiSelected.value = !!objectType.value.find(item => item.value === 'KASI')

  submitDisabled.value = !(
    dateFilter.value.from ||
    dateFilter.value.to ||
    objectType.value.length ||
    hasBuildings.value.length ||
    isFilterDirty(appartmentArea.value, SEARCH_SLIDER_LIMITS.korteri_pind) ||
    isFilterDirty(
      totalArea.value,
      SEARCH_SLIDER_LIMITS.pindala[areaUnit.value],
    ) ||
    (priceMode.value === 'total'
      ? price.value.from !== '' || price.value.to !== ''
      : price.value.from !== '' || price.value.to !== '')
  )
}

const submit = () => {
  loading.value = true
  // Set loading state in map store
  mapStore.$patch({
    loading: true,
  })

  buildFilterParams()

  transactionMapRequestParams.value.label = activeLabel.value

  emit('update:map-features')
  emit('search')

  loading.value = false
  transactionStore.shouldInitTransactionFilters = true
}

const reset = () => {
  dateFilter.value = {
    from: null,
    to: null,
  }
  dateFilterFrom.value = null
  dateFilterTo.value = null
  activeDateShortcut.value = null
  objectType.value = []
  hasBuildings.value = []
  appartmentArea.value = deepCopy(SEARCH_SLIDER_LIMITS.korteri_pind)
  totalArea.value = deepCopy(SEARCH_SLIDER_LIMITS.pindala[SEARCH_DEFAULT_UNIT])
  areaUnit.value = SEARCH_DEFAULT_UNIT
  price.value = {
    from: '',
    to: '',
  }
  priceMode.value = 'total'
  priceUnitType.value = 'apartment'
  ehrValues.value = deepCopy(SEARCH_EHR_DEFAULTS)
  filterChange()

  // Clear map features and reset transaction store
  mapStore.$patch({
    features: [],
    loading: false,
  })

  transactionMapRequestParams.value.filters = {}

  transactionStore.$patch({
    searchMetadata: {
      filterOptions: {},
      ranges: {},
      count: 0,
    },
  })
}

const buildFilterParams = () => {
  // Date filters (cleared pickers must also drop their stale filter entry)
  if (dateFilter.value.from) {
    transactionMapRequestParams.value.filters['startDate'] = {
      key: 'startDate',
      type: 'date',
      value: dateFilterFrom.value,
    }
  } else {
    delete transactionMapRequestParams.value.filters['startDate']
  }
  if (dateFilter.value.to) {
    transactionMapRequestParams.value.filters['endDate'] = {
      key: 'endDate',
      type: 'date',
      value: dateFilterTo.value,
    }
  } else {
    delete transactionMapRequestParams.value.filters['endDate']
  }

  // Object type filter
  if (objectType.value.length) {
    transactionMapRequestParams.value.filters['objekti_liik'] = {
      key: 'objekti_liik',
      type: 'multiselect',
      value: objectType.value.map(item => item.value),
    }
  } else if (transactionMapRequestParams.value.filters?.['objekti_liik']) {
    delete transactionMapRequestParams.value.filters['objekti_liik']
  }

  // Building status filter
  if (hasBuildings.value.length) {
    transactionMapRequestParams.value.filters['hoonestatud'] = {
      key: 'hoonestatud',
      type: 'multiselect',
      value: hasBuildings.value.map(item => item.value),
    }
  } else if (transactionMapRequestParams.value.filters?.['hoonestatud']) {
    delete transactionMapRequestParams.value.filters['hoonestatud']
  }

  // Area filters
  if (isFilterDirty(appartmentArea.value, SEARCH_SLIDER_LIMITS.korteri_pind)) {
    transactionMapRequestParams.value.filters['korteri_pind'] = {
      key: 'korteri_pind',
      type: 'range',
      value: appartmentArea.value,
    }
  } else {
    // A cleared range must leave the request too, or it filters every later search.
    delete transactionMapRequestParams.value.filters['korteri_pind']
  }

  // The area filter matches all transactions regardless of stored unit
  // (pindala_yhik M/H); areaUnit only says which unit the bounds are in.
  // The price section below re-adds a pindala_yhik filter for land €/m2.
  delete transactionMapRequestParams.value.filters['pindala_yhik']
  if (
    isFilterDirty(totalArea.value, SEARCH_SLIDER_LIMITS.pindala[areaUnit.value])
  ) {
    transactionMapRequestParams.value.filters['pindala'] = {
      key: 'pindala',
      type: 'area_range',
      value: totalArea.value,
      unit: areaUnit.value,
    }
  } else {
    delete transactionMapRequestParams.value.filters['pindala']
  }

  // Price filter
  if (priceMode.value === 'total') {
    if (price.value.from || price.value.to) {
      transactionMapRequestParams.value.filters['tehingusumma'] = {
        key: 'tehingusumma',
        type: 'range',
        value: [price.value.from || '', price.value.to || ''],
      }
    } else {
      delete transactionMapRequestParams.value.filters['tehingusumma']
    }
    // remove unit price filters if present
    delete transactionMapRequestParams.value.filters['hind_m2_korter']
    delete transactionMapRequestParams.value.filters['hind_yhik']
  } else {
    // unit mode
    delete transactionMapRequestParams.value.filters['tehingusumma']
    if (priceUnitType.value === 'apartment') {
      if (price.value.from || price.value.to) {
        transactionMapRequestParams.value.filters['hind_m2_korter'] = {
          key: 'hind_m2_korter',
          type: 'range',
          value: [price.value.from || '', price.value.to || ''],
        }
      } else {
        delete transactionMapRequestParams.value.filters['hind_m2_korter']
      }
      delete transactionMapRequestParams.value.filters['hind_yhik']
    } else {
      // land €/m2, force pindala_yhik to 'M'
      if (price.value.from || price.value.to) {
        transactionMapRequestParams.value.filters['hind_yhik'] = {
          key: 'hind_yhik',
          type: 'range',
          value: [price.value.from || '', price.value.to || ''],
        }
        transactionMapRequestParams.value.filters['pindala_yhik'] = {
          key: 'pindala_yhik',
          type: 'value',
          value: 'M',
        }
      } else {
        delete transactionMapRequestParams.value.filters['hind_yhik']
      }
      delete transactionMapRequestParams.value.filters['hind_m2_korter']
    }
  }

  // EHR filters
  if (ehrValues.value.year.from || ehrValues.value.year.to) {
    transactionMapRequestParams.value.filters['ehr_aasta'] = {
      key: 'ehr_aasta',
      type: 'range',
      value: [ehrValues.value.year.from || '', ehrValues.value.year.to || ''],
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_aasta']
  }

  if (ehrValues.value.floors.from || ehrValues.value.floors.to) {
    transactionMapRequestParams.value.filters['ehr_korruseid'] = {
      key: 'ehr_korruseid',
      type: 'range',
      value: [
        ehrValues.value.floors.from || '',
        ehrValues.value.floors.to || '',
      ],
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_korruseid']
  }

  if (ehrValues.value.floor.from || ehrValues.value.floor.to) {
    transactionMapRequestParams.value.filters['ehr_korrus'] = {
      key: 'ehr_korrus',
      type: 'range',
      value: [ehrValues.value.floor.from || '', ehrValues.value.floor.to || ''],
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_korrus']
  }

  if (ehrValues.value.rooms.from || ehrValues.value.rooms.to) {
    transactionMapRequestParams.value.filters['ehr_tube'] = {
      key: 'ehr_tube',
      type: 'range',
      value: [ehrValues.value.rooms.from || '', ehrValues.value.rooms.to || ''],
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_tube']
  }

  if (ehrValues.value.partArea.from || ehrValues.value.partArea.to) {
    transactionMapRequestParams.value.filters['ehr_osa_pind'] = {
      key: 'ehr_osa_pind',
      type: 'range',
      value: [
        ehrValues.value.partArea.from || '',
        ehrValues.value.partArea.to || '',
      ],
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_osa_pind']
  }

  // EHR multiselect filters
  if (ehrValues.value.construction.length) {
    transactionMapRequestParams.value.filters['ehr_konstruktsioon'] = {
      key: 'ehr_konstruktsioon',
      type: 'multiselect',
      value: ehrValues.value.construction.map(item => item.value),
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_konstruktsioon']
  }

  if (ehrValues.value.purpose.length) {
    transactionMapRequestParams.value.filters['ehr_purpose'] = {
      key: 'ehr_purpose',
      type: 'multiselect',
      value: ehrValues.value.purpose.map(item => item.value),
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_purpose']
  }

  if (ehrValues.value.partPurpose.length) {
    transactionMapRequestParams.value.filters['ehr_part_purpose'] = {
      key: 'ehr_part_purpose',
      type: 'multiselect',
      value: ehrValues.value.partPurpose.map(item => item.value),
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_part_purpose']
  }

  if (ehrValues.value.wall.length) {
    transactionMapRequestParams.value.filters['ehr_sein'] = {
      key: 'ehr_sein',
      type: 'multiselect',
      value: ehrValues.value.wall.map(item => item.value),
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_sein']
  }

  if (ehrValues.value.partHeating.length) {
    transactionMapRequestParams.value.filters['ehr_osa_soojus'] = {
      key: 'ehr_osa_soojus',
      type: 'multiselect',
      value: ehrValues.value.partHeating.map(item => item.value),
    }
  } else {
    delete transactionMapRequestParams.value.filters['ehr_osa_soojus']
  }
}

// Date range shortcuts - sets both start and end dates
const setDateRangeShortcut = months => {
  const today = new Date()
  const fromDate = subMonths(today, months)
  dateFilter.value.from = fromDate
  dateFilter.value.to = today
  // Also set the ISO formatted values used by buildFilterParams
  dateFilterFrom.value = format(fromDate, 'yyyy-MM-dd')
  dateFilterTo.value = format(today, 'yyyy-MM-dd')
  filterChange()
  activeDateShortcut.value = months
  submit()
}

// RED-255: opening "Tehingute valimine kaardilt" on a pristine search
// pre-selects the evaluation's own market segment and runs the 12-kuud
// default search — a relevant map with zero clicks. Applied at most once
// per mount and never over user-touched filters. The standalone
// /transactions page is unaffected: the dialog flag never flips there.
const appliedDialogDefaults = ref(false)

const findFilterOption = (key, value) => {
  const filter = TRANSACTIONS_FILTERS.find(item => item.key === key)
  return filter?.options?.find(option => option.value === value) ?? null
}

const applyDialogDefaults = async () => {
  if (appliedDialogDefaults.value || !transactionStore.isTransactionsMapDialogOpen) {
    return
  }
  appliedDialogDefaults.value = true

  const pristine =
    !objectType.value.length &&
    !hasBuildings.value.length &&
    !dateFilter.value.from &&
    !dateFilter.value.to &&
    !Object.keys(transactionMapRequestParams.value.filters || {}).length
  if (!pristine) {
    return
  }

  // ER/MR adob_liik = apartment-kind address; isLandParcel is the
  // authoritative maatükk flag (never derivable from empty EHR data).
  const isApartment =
    !evaluationStore.isLandParcel &&
    (evaluationStore.selectedBuildingPart !== null ||
      ['ER', 'MR'].includes(evaluationStore.objectType))
  const liikOption = findFilterOption('objekti_liik', isApartment ? 'KOM' : 'KASI')
  if (liikOption) {
    objectType.value = [liikOption]
  }
  if (evaluationStore.isLandParcel) {
    const unbuiltOption = findFilterOption('hoonestatud', 'F')
    if (unbuiltOption) {
      hasBuildings.value = [unbuiltOption]
    }
  }
  filterChange()

  // Let the sibling map component mount before submit() reaches it.
  await nextTick()
  setDateRangeShortcut(12)
}

onMounted(() => {
  applyDialogDefaults()
})

watch(() => transactionStore.isTransactionsMapDialogOpen, open => {
  if (open) {
    applyDialogDefaults()
  }
})
</script>

<style scoped>
.address-autocomplete :deep(.p-autocomplete) {
  width: 100%;
}

.address-autocomplete :deep(.p-autocomplete-panel) {
  width: 100%;
}
</style>
